import { Edge, Node, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dagre from "@dagrejs/dagre";
import { useWorkflowForm } from "../context/workflows-editor-workflows-context";
import { TRIGGER_NODE_HEIGHT_ESTIMATION, TRIGGER_NODE_WIDTH } from "../nodes/trigger/editor";
import { CONDITION_NODE_HEIGHT_ESTIMATION, CONDITION_NODE_WIDTH } from "../nodes/condition/editor";
import { EMAIL_TEMPLATE_NODE_HEIGHT_ESTIMATION, EMAIL_TEMPLATE_NODE_WIDTH } from "../nodes/email-action/editor";
import { RETURN_EMAIL_NODE_HEIGHT_ESTIMATION, RETURN_EMAIL_NODE_WIDTH } from "../nodes/return-email-action/editor";
import { RETURN_ORDER_NOTE_NODE_HEIGHT_ESTIMATION, RETURN_ORDER_NOTE_NODE_WIDTH } from "../nodes/return-order-note-action/editor";
import { RETURN_ORDER_ATTRIBUTE_NODE_HEIGHT_ESTIMATION, RETURN_ORDER_ATTRIBUTE_NODE_WIDTH } from "../nodes/return-order-attribute-action/editor";
import { RETURN_AUTO_TREAT_NODE_HEIGHT_ESTIMATION, RETURN_AUTO_TREAT_NODE_WIDTH } from "../nodes/return-auto-treat-action/editor";
import { WEB_TEMPLATE_NODE_HEIGHT_ESTIMATION, WEB_TEMPLATE_NODE_WIDTH } from "../nodes/tracking-page-action/editor";
import {
  WALLET_NOTIFICATION_NODE_HEIGHT_ESTIMATION,
  WALLET_NOTIFICATION_NODE_WIDTH,
} from "../nodes/wallet-notification-action/editor";
import {
  ActionNodeDataDto,
  ConditionNodeDataDto,
  WorkflowV2Dto,
  WorkflowV2NodeDto,
} from "@babackapp/private-api-client/types";
import { EditorNodeType } from "../nodes";
import { resolveEditorNodeType } from "../utils/node-resolver";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import useCurrentShopSuspense from "./use-current-shop-suspense";
import { GORGIAS_ACTION_NODE_HEIGHT_ESTIMATION, GORGIAS_ACTION_NODE_WIDTH } from "../nodes/gorgias-action/editor";
import { ZENDESK_ACTION_NODE_HEIGHT_ESTIMATION, ZENDESK_ACTION_NODE_WIDTH } from "../nodes/zendesk-action/editor";
import { KLAVIYO_ACTION_NODE_HEIGHT_ESTIMATION, KLAVIYO_ACTION_NODE_WIDTH } from "../nodes/klaviyo-action/editor";
import { MODIFY_ORDER_TAGS_NODE_HEIGHT_ESTIMATION, MODIFY_ORDER_TAGS_NODE_WIDTH } from "../nodes/modify-order-tags-action/editor";


const NODE_DIMENSIONS_MAP: Record<
  EditorNodeType,
  { width: number; height: number }
> = {
  trigger: {
    width: TRIGGER_NODE_WIDTH,
    height: TRIGGER_NODE_HEIGHT_ESTIMATION,
  },
  condition: {
    width: CONDITION_NODE_WIDTH,
    height: CONDITION_NODE_HEIGHT_ESTIMATION,
  },
  "action:send_tracking_email": {
    width: EMAIL_TEMPLATE_NODE_WIDTH,
    height: EMAIL_TEMPLATE_NODE_HEIGHT_ESTIMATION,
  },
  "action:send_return_email": {
    width: RETURN_EMAIL_NODE_WIDTH,
    height: RETURN_EMAIL_NODE_HEIGHT_ESTIMATION,
  },
  "action:set_order_note": {
    width: RETURN_ORDER_NOTE_NODE_WIDTH,
    height: RETURN_ORDER_NOTE_NODE_HEIGHT_ESTIMATION,
  },
  "action:set_order_attribute": {
    width: RETURN_ORDER_ATTRIBUTE_NODE_WIDTH,
    height: RETURN_ORDER_ATTRIBUTE_NODE_HEIGHT_ESTIMATION,
  },
  "action:auto_treat_return": {
    width: RETURN_AUTO_TREAT_NODE_WIDTH,
    height: RETURN_AUTO_TREAT_NODE_HEIGHT_ESTIMATION,
  },
  "action:update_web": {
    width: WEB_TEMPLATE_NODE_WIDTH,
    height: WEB_TEMPLATE_NODE_HEIGHT_ESTIMATION,
  },
  "action:send_wallet_notification": {
    width: WALLET_NOTIFICATION_NODE_WIDTH,
    height: WALLET_NOTIFICATION_NODE_HEIGHT_ESTIMATION,
  },
  "action:create_ticket_on_gorgias": {
    width: GORGIAS_ACTION_NODE_WIDTH,
    height: GORGIAS_ACTION_NODE_HEIGHT_ESTIMATION
  },
  "action:create_ticket_on_zendesk": {
    width: ZENDESK_ACTION_NODE_WIDTH,
    height: ZENDESK_ACTION_NODE_HEIGHT_ESTIMATION
  },
  "action:send_klaviyo_event": {
    width: KLAVIYO_ACTION_NODE_WIDTH,
    height: KLAVIYO_ACTION_NODE_HEIGHT_ESTIMATION
  },
  "action:modify_order_tags": {
    width: MODIFY_ORDER_TAGS_NODE_WIDTH,
    height: MODIFY_ORDER_TAGS_NODE_HEIGHT_ESTIMATION
  },
};

const DEFAULT_NODE_DIMENSIONS = { width: 250, height: 150 };

function getNodeDimensions(editorNodeType?: EditorNodeType): {
  width: number;
  height: number;
} {
  if (!editorNodeType) return DEFAULT_NODE_DIMENSIONS;
  return NODE_DIMENSIONS_MAP[editorNodeType] || DEFAULT_NODE_DIMENSIONS;
}

const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = "LR",
) => {
  const isHorizontal = direction === "LR";

  // Create a new graph instance each time to ensure clean layout
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 50, // Horizontal spacing between nodes
    ranksep: 100, // Vertical spacing between ranks
  });

  // Add all nodes
  nodes.forEach((node) => {
    const { width, height } = getNodeDimensions(node.type as EditorNodeType);
    dagreGraph.setNode(node.id, { width, height });
  });

  // Add all edges
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const newNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const { width, height } = getNodeDimensions(node.type as EditorNodeType);

    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: newNodes, edges };
};

/**
 * Maps an API node to a ReactFlow node using the resolver.
 * This is a generic mapper that works for all node types.
 */
function mapApiNodeToReactFlowNode(apiNode: WorkflowV2NodeDto): Node {
  const editorNodeType = resolveEditorNodeType(apiNode);

  // Map the data based on node type
  let nodeData: Node["data"];

  if (apiNode.type === "action") {
    const actionData = apiNode.data as ActionNodeDataDto;
    nodeData = {
      label: actionData.label,
      templateId: "templateId" in actionData.config ? actionData.config.templateId : undefined,
      titleContent: "titleContent" in actionData.config ? actionData.config.titleContent : undefined,
      textContent: "textContent" in actionData.config ? actionData.config.textContent : undefined,
      overrides: "overrides" in actionData.config ? actionData.config.overrides : undefined,
    };
  } else if (apiNode.type === "condition") {
    const conditionData = apiNode.data as ConditionNodeDataDto;
    nodeData = {
      label: conditionData.label,
      conditions: conditionData.conditions,
      operator: conditionData.operator,
    };
  } else {
    nodeData = { label: "Unknown node" };
  }

  return {
    id: apiNode.id,
    type: editorNodeType,
    position: { x: 0, y: 0 },
    data: nodeData,
  };
}

function transformWorkflowToReactFlow(
  workflowTriggers: WorkflowV2Dto["trigger"][],
  workflowNodes: WorkflowV2Dto["definition"]["nodes"],
  workflowEdges: WorkflowV2Dto["definition"]["edges"],
): { nodes: Node[]; edges: Edge[] } {
  if (!workflowTriggers || !workflowNodes || !workflowEdges) {
    return { nodes: [], edges: [] };
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  workflowTriggers.forEach((trigger) => {
    // Map trigger node
    let triggerLabel = trigger?.data.label;
    if (!triggerLabel) {
      if (trigger?.data.triggerType === 'tracking_status_change' && 'trackingStatus' in trigger.data.config) {
        triggerLabel = `Status: ${trigger.data.config.trackingStatus}`;
      } else if (trigger?.data.triggerType === 'order_not_fulfilled' && 'minDaysSinceCreation' in trigger.data.config) {
        triggerLabel = `Days: ${trigger.data.config.minDaysSinceCreation}`;
      }
    }

    nodes.push({
      id: trigger.id,
      type: resolveEditorNodeType(trigger),
      position: { x: 0, y: 0 },
      data: {
        label: triggerLabel,
        triggerType: trigger.data.triggerType,
        config: trigger.data.config,
      },
    });
  });

  // Map all workflow nodes using the generic mapper
  workflowNodes.forEach((node) => {
    nodes.push(mapApiNodeToReactFlowNode(node));
  });

  workflowEdges.forEach((edge) => {
    edges.push({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle || undefined,
      targetHandle: edge.targetHandle || undefined,
      type: edge.type || "default",
      animated: edge.animated || false,
    });
  });

  // Sort edges: true edges first, then others, then false edges
  const sortedEdges = edges.sort((a, b) => {
    if (a.sourceHandle === "true" && b.sourceHandle !== "true") return -1;
    if (a.sourceHandle !== "true" && b.sourceHandle === "true") return 1;
    if (a.sourceHandle === "false" && b.sourceHandle !== "false") return 1;
    if (a.sourceHandle !== "false" && b.sourceHandle === "false") return -1;
    return 0;
  });

  // Sort nodes: nodes targeted by true edges first, then others, then false edges
  const trueTargets = new Set(
    sortedEdges.filter((e) => e.sourceHandle === "true").map((e) => e.target),
  );
  const falseTargets = new Set(
    sortedEdges.filter((e) => e.sourceHandle === "false").map((e) => e.target),
  );

  const sortedNodes = nodes.sort((a, b) => {
    const aIsTrue = trueTargets.has(a.id);
    const aIsFalse = falseTargets.has(a.id);
    const bIsTrue = trueTargets.has(b.id);
    const bIsFalse = falseTargets.has(b.id);

    if (aIsTrue && !bIsTrue) return -1;
    if (!aIsTrue && bIsTrue) return 1;
    if (aIsFalse && !bIsFalse) return 1;
    if (!aIsFalse && bIsFalse) return -1;
    return 0;
  });

  return { nodes: sortedNodes, edges: sortedEdges };
}

export default function useWorkflowData(): { nodes: Node[]; edges: Edge[] } {
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { api } = useWorkflowsEditorConfig();
  const { data: workflows } = api.useWorkflowsSuspense(shop.id);
  const form = useWorkflowForm();
  const workflow = form.watch("workflow");

  const sortedWorkflows = useMemo(() => {
    if (!workflows) return [];
    // isActive workflows first, then by name
    return [...workflows].sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [workflows]);

  const { nodes, edges } = useMemo(() => {
    // When no workflow is selected, show all workflows in a grouped layout
    // Groups Vector is calculated based on the layout of each workflow
    if (!workflow?.definition) {
      if (sortedWorkflows?.length) {
        const allNodes: Node[] = [];
        const allEdges: Edge[] = [];
        let currentYOffset = 0;
        const WORKFLOW_SPACING = 80;
        const WORKFLOW_TOP_OFFSET = 0; // To give some space at the top for the group label
        const WORKFLOW_GROUP_Y_PADDING = 50;
        const WORKFLOW_GROUP_X_PADDING = 40;

        sortedWorkflows.forEach((wf) => {
          const parentId = `workflow-group-${wf.id}`;
          const activeEdges = (wf.definition.edges || []).map((e) => ({
            ...e,
            animated: wf.isActive,
          }));

          const { nodes: workflowNodes, edges: workflowEdges } =
            transformWorkflowToReactFlow(
              [wf.trigger],
              wf.definition.nodes,
              activeEdges,
            );

          const { nodes: layoutedNodes, edges: layoutedEdges } =
            getLayoutedElements(workflowNodes, workflowEdges, "LR");

          // Calculate bounds for group node
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          layoutedNodes.forEach((node) => {
            const dims = getNodeDimensions(node.type as EditorNodeType);
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + dims.width);
            maxY = Math.max(maxY, node.position.y + dims.height);
          });

          const width = maxX - minX + WORKFLOW_GROUP_X_PADDING * 2;
          const height =
            maxY - minY + WORKFLOW_GROUP_Y_PADDING * 2 + WORKFLOW_TOP_OFFSET;

          allNodes.push({
            id: parentId,
            type: "group",
            position: { x: 50, y: currentYOffset + 30 },
            data: { label: wf.name, workflowId: wf.id, isActive: wf.isActive },
            style: {
              width,
              height,
              border: "none",
              backgroundColor: "transparent",
            },
          });

          layoutedNodes.forEach((node) => {
            allNodes.push({
              ...node,
              parentId: parentId,
              extent: "parent" as const,
              position: {
                x: node.position.x - minX + WORKFLOW_GROUP_X_PADDING,
                y:
                  node.position.y -
                  minY +
                  WORKFLOW_GROUP_Y_PADDING +
                  WORKFLOW_TOP_OFFSET,
              },
              style: {
                pointerEvents: "none", // We don't want nodes to be interactable in grouped view
                opacity: wf.isActive ? 1 : 0.8,
              },
            });
          });

          allEdges.push(...layoutedEdges);
          currentYOffset += height + WORKFLOW_SPACING;
        });

        return { nodes: allNodes, edges: allEdges };
      }

      return { nodes: [], edges: [] };
    }

    const { nodes: rawNodes, edges: rawEdges } = transformWorkflowToReactFlow(
      [workflow.trigger],
      workflow.definition.nodes,
      workflow.definition.edges,
    );

    return getLayoutedElements(rawNodes, rawEdges, "LR");
  }, [
    workflow,
    workflow?.definition?.edges,
    workflow?.definition?.nodes,
    workflow?.trigger?.data,
    t,
    sortedWorkflows,
  ]);

  return {
    nodes,
    edges,
  };
}
